import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import DataTable from "react-data-table-component";
import { Container, Input } from "reactstrap";
import { toast } from "react-toastify";
import { Breadcrumbs, Btn } from "../../../AbstractElements";
import LabelService from "../../../Services/label/LabelService";
import SpinnerLoader from "../../Loaders/Spinner";
import LabelDetailModal from "./LabelDetailModal";
import "./labels.css";

import { getApiErrorMessage, toUserMessage } from "../../../utils/apiErrorMessage";

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const ColorSwatch = ({ color }) => (
  <span className="lbl-swatch" title={color} style={{ background: color }} />
);

const useViewport = () => {
  const [width, setWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );

  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return {
    isCompact: width < 768,
    isMedium: width >= 768 && width < 1200,
    isWide: width >= 1200,
  };
};

const tableStyles = {
  table: {
    style: {
      width: "100%",
    },
  },
  headCells: {
    style: {
      fontSize: "0.8125rem",
      fontWeight: 600,
      paddingLeft: "10px",
      paddingRight: "10px",
    },
  },
  cells: {
    style: {
      fontSize: "0.875rem",
      paddingLeft: "10px",
      paddingRight: "10px",
    },
  },
};

function LabelsContainer() {
  const queryClient = useQueryClient();
  const { isCompact, isMedium, isWide } = useViewport();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLabel, setSelectedLabel] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  const { isPending, isError, data, error } = useQuery({
    queryKey: ["labels"],
    queryFn: LabelService.getLabels,
  });

  const syncMutation = useMutation({
    mutationFn: LabelService.syncLabels,
    onSuccess: (response) => {
      if (response.success) {
        toast.success(
          toUserMessage(response.data?.message, "Labels synced successfully")
        );
        queryClient.invalidateQueries(["labels"]);
      } else {
        toast.error(toUserMessage(response, "Failed to sync labels"));
      }
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, "Failed to sync labels"));
    },
  });

  const updateRefMutation = useMutation({
    mutationFn: LabelService.updateLabelInvoiceRef,
    onSuccess: () => {
      toast.success("Invoice reference updated");
      queryClient.invalidateQueries(["labels"]);
      setIsDetailOpen(false);
    },
    onError: (err) => {
      toast.error(
        getApiErrorMessage(err, "Failed to update invoice reference")
      );
    },
  });

  const labels = data?.data ?? [];

  const summary = useMemo(() => {
    const totalArtists = labels.reduce(
      (sum, label) => sum + (Number(label.artists_count) || 0),
      0
    );

    return {
      total: labels.length,
      withEmail: labels.filter((label) => label.email).length,
      totalArtists,
    };
  }, [labels]);

  const filteredLabels = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return labels;

    return labels.filter((label) =>
      [
        label.label_name,
        label.name,
        label.email,
        label.label_red_id,
        label.label_Inv_ref,
        label.theme_name,
        label.invoice_theme_name,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term))
    );
  }, [labels, searchTerm]);

  const handleViewLabel = (label) => {
    setSelectedLabel(label);
    setIsDetailOpen(true);
  };

  const handleSaveRef = ({ id, label_Inv_ref }) => {
    updateRefMutation.mutate({ id, label_Inv_ref });
  };

  const tableColumns = useMemo(() => {
    const columns = [
      {
        name: "Label",
        selector: (row) => row.label_name,
        sortable: true,
        grow: isCompact ? 2 : 1.6,
        cell: (row) => {
          const name = row.label_name || row.name || "—";
          return (
            <div className="lbl-name-cell">
              <div className="lbl-name-cell__name" title={name}>
                {name}
              </div>
              {row.label_red_id ? (
                <div className="lbl-name-cell__meta">CMS ID: {row.label_red_id}</div>
              ) : null}
            </div>
          );
        },
      },
    ];

    if (!isCompact) {
      columns.push({
        name: "Theme",
        selector: (row) => row.theme_name,
        sortable: true,
        grow: 1.1,
        cell: (row) => (
          <div className="lbl-theme-cell">
            <div
              className="lbl-theme-cell__name"
              title={row.theme_name || undefined}
            >
              {row.theme_name || "—"}
            </div>
            {row.label_theme ? (
              <div className="lbl-theme-cell__meta">
                Template #{row.label_theme}
              </div>
            ) : null}
          </div>
        ),
      });
    }

    if (isWide) {
      columns.push({
        name: "Colors",
        center: true,
        width: "84px",
        cell: (row) => (
          <div className="d-flex justify-content-center gap-1">
            {row.color1 ? <ColorSwatch color={row.color1} /> : null}
            {row.color2 ? <ColorSwatch color={row.color2} /> : null}
            {!row.color1 && !row.color2 ? "—" : null}
          </div>
        ),
      });
    }

    if (!isCompact) {
      columns.push({
        name: "Email",
        selector: (row) => row.email,
        sortable: true,
        grow: 1.4,
        cell: (row) =>
          row.email ? (
            <span className="lbl-email-cell" title={row.email}>
              {row.email}
            </span>
          ) : (
            "—"
          ),
      });
    }

    columns.push({
      name: "Artists",
      selector: (row) => row.artists_count,
      sortable: true,
      center: true,
      width: isCompact ? "72px" : "80px",
      cell: (row) => {
        const count = Number(row.artists_count) || 0;
        return count > 0 ? count : "—";
      },
    });

    if (isWide || isMedium) {
      columns.push({
        name: "Invoice ref",
        selector: (row) => row.label_Inv_ref,
        sortable: true,
        grow: 0.9,
        cell: (row) =>
          row.label_Inv_ref ? (
            <span className="lbl-ref-cell" title={row.label_Inv_ref}>
              {row.label_Inv_ref}
            </span>
          ) : (
            "—"
          ),
      });
    }

    if (isWide) {
      columns.push({
        name: "Updated",
        selector: (row) => row.updated_at,
        sortable: true,
        width: "110px",
        cell: (row) => formatDate(row.updated_at),
      });
    }

    columns.push({
      name: "Actions",
      center: true,
      width: "72px",
      cell: (row) => (
        <div className="lbl-actions">
          <button
            type="button"
            className="lbl-action-btn"
            title="Manage label"
            aria-label={`Manage ${row.label_name || row.name || "label"}`}
            onClick={() => handleViewLabel(row)}
          >
            <i className="fa fa-cog" aria-hidden="true" />
          </button>
        </div>
      ),
    });

    return columns;
  }, [isCompact, isMedium, isWide]);

  if (isError) {
    return (
      <div className="alert alert-danger m-3">
        Failed to load labels: {error?.message || "Unknown error"}
      </div>
    );
  }

  return (
    <>
      <Breadcrumbs
        parent="Labels"
        title="Labels"
        mainTitle="Labels"
        buttons={[
          <Btn
            key="sync-labels"
            attrBtn={{
              size: "sm",
              color: "primary",
              onClick: () => syncMutation.mutate(),
              disabled: syncMutation.isPending,
            }}
          >
            <i className="fa fa-refresh me-1" />
            {syncMutation.isPending ? "Syncing..." : "Sync from CMS"}
          </Btn>,
        ]}
      />

      <div className="rd-labels-list">
        <Container fluid>
          <div className="lbl-stats">
            <div className="lbl-stat">
              <p className="lbl-stat__label">Total labels</p>
              <p className="lbl-stat__value">{summary.total}</p>
            </div>
            <div className="lbl-stat">
              <p className="lbl-stat__label">With email</p>
              <p className="lbl-stat__value">{summary.withEmail}</p>
            </div>
            <div className="lbl-stat">
              <p className="lbl-stat__label">Linked artists</p>
              <p className="lbl-stat__value">{summary.totalArtists}</p>
            </div>
          </div>

          <div className="lbl-toolbar">
            <div className="lbl-toolbar__search">
              <Input
                type="search"
                placeholder="Search label, theme, email, CMS ID…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="lbl-panel">
            {isPending ? (
              <SpinnerLoader />
            ) : filteredLabels.length === 0 ? (
              <div className="lbl-empty">No labels match your search.</div>
            ) : (
              <div className="lbl-table-wrap">
                <DataTable
                  columns={tableColumns}
                  data={filteredLabels}
                  pagination
                  paginationPerPage={25}
                  paginationRowsPerPageOptions={[10, 25, 50]}
                  highlightOnHover
                  responsive
                  dense={isCompact}
                  customStyles={tableStyles}
                  noDataComponent="No labels found."
                />
              </div>
            )}
          </div>
        </Container>
      </div>

      <LabelDetailModal
        isOpen={isDetailOpen}
        toggle={() => setIsDetailOpen(false)}
        label={selectedLabel}
        onSaveRef={handleSaveRef}
        isSaving={updateRefMutation.isPending}
      />
    </>
  );
}

export default LabelsContainer;
